import React, { useMemo, useState } from 'react';

import { DataContext, DataContextValue } from './useDataContext';
import { AddressListActions, useAddressListReducer } from './useAddressListReducer';
import { CheckDataStatus } from '../@types/checkData';
import { GetAddressParams, getAddressList } from '../api/address';
import { useAbortController } from '../hooks/useAbortController';
import { useDebouncedCallback } from '../hooks/useDebouncedCallback';
import { AddressWithUnom } from '../@types/address';
import { sendStats, STATS_EVENTS } from '../helpers/sendStats';

type Props = {
  children: React.ReactNode;
};

export const DataProvider = ({ children }: Props) => {
  const [searchValue, setSearchValue] = useState('');
  const [checkDataStatus, setCheckDataStatus] = useState(CheckDataStatus.Initial);
  const [selectedAddress, setSelectedAddress] = useState<DataContextValue['selectedAddress']>(null);
  const [addressListState, dispatchAddressList] = useAddressListReducer();
  const { getAbortSignal, abortRequest } = useAbortController();

  const { clearDebouncedCall, debouncedCallbackWrapper: debouncedGetAddress } =
    useDebouncedCallback((params: Omit<GetAddressParams, 'signal'>) => {
      const signal = getAbortSignal();
      dispatchAddressList({ type: AddressListActions.Fetch });
      getAddressList(params)
        .then((addressList) => {
          if (!signal?.aborted) {
            dispatchAddressList({ type: AddressListActions.SetData, value: addressList });
          }
        })
        .catch(() => {
          if (!signal?.aborted) {
            dispatchAddressList({ type: AddressListActions.SetError });
          }
        });
    });

  const abortGetAddress = () => {
    clearDebouncedCall();
    abortRequest();
    dispatchAddressList({ type: AddressListActions.Reset });
  };

  const onSelectValidAddress = (address: AddressWithUnom) => {
    sendStats(STATS_EVENTS.ADDRESS_SELECT, address);
    setSelectedAddress(address);
    dispatchAddressList({ type: AddressListActions.SetData, value: [address] });
  };

  const resetSelectedAddress = () => {
    setSelectedAddress(null);
  };

  const context = useMemo(
    (): DataContextValue => ({
      searchValue,
      checkDataStatus,
      selectedAddress,
      ...addressListState,
      onSelectValidAddress,
      resetSelectedAddress,
      setCheckDataStatus,
      setSearchValue,
      debouncedGetAddress,
      abortGetAddress,
    }),
    [selectedAddress, addressListState, checkDataStatus, searchValue],
  );
  return <DataContext.Provider value={context}>{children}</DataContext.Provider>;
};
